import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Card, Descriptions, Empty, Form, Input, InputNumber, Select, Space, Spin, Tag, Typography, message } from "antd";
import { CheckCircleOutlined, SendOutlined } from "@ant-design/icons";
import { useSocket } from "../../../contexts/SocketContext";
import { AUTHORIZATION_LABELS } from "../../models/shared/operationsTaxonomy";
import { useOperationsHandover } from "../../viewmodels/handover/useOperationsHandover";
import { useAccess } from "../../../../features/access/context/AccessProvider";

const money = value => `AED ${Number(value || 0).toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const makeKey = queryPk => window.crypto?.randomUUID?.() || `handover-${queryPk}-${Date.now()}-${Math.random().toString(16).slice(2)}`;

function addressLabel(address) {
  return [address.name, address.type, address.address].filter(Boolean).join(" · ");
}

export function OperationsHandoverPanel({ queryPk, customer, priority = 1, embedded = false, onStatusChange, canSubmit: canSubmitProp }) {
  const { socket } = useSocket();
  const { can } = useAccess();
  const canSubmit = canSubmitProp ?? can("sales_pipeline.handover_to_operations");
  const { projection, loading, saving, error, reload, saveAddresses, handover, applyApproved } = useOperationsHandover(queryPk);
  const [addressValues, setAddressValues] = useState({});
  const idempotencyKeyRef = useRef(null);
  const [form] = Form.useForm();
  const addresses = useMemo(() => customer?.customer_addresses_new || [], [customer]);

  useEffect(() => {
    if (!projection?.lines) return;
    setAddressValues(Object.fromEntries(projection.lines.map(line => [line.query_part_id, line.address_id || undefined])));
    form.setFieldsValue({ authorization_basis: projection.allowed_bases?.[0], priority: Number(priority) || 1 });
  }, [projection, priority, form]);

  useEffect(() => {
    if (!socket || !queryPk) return undefined;
    const refresh = payload => {
      const data = payload?.data || payload;
      const eventQueryPk = data?.query_pk ?? data?.handover?.query_pk ?? data?.submitted_payload?.query_pk;
      if (!eventQueryPk || Number(eventQueryPk) === Number(queryPk)) reload().catch(() => {});
    };
    socket.on("operations_v2:handover_submitted", refresh);
    socket.on("operations_v2:handover_authorized", refresh);
    return () => {
      socket.off("operations_v2:handover_submitted", refresh);
      socket.off("operations_v2:handover_authorized", refresh);
    };
  }, [socket, queryPk, reload]);

  const getBlockedReason = current => {
    if (!current) return "Checking handover readiness…";
    if (current.state === "awaiting_approval") return "Handover is awaiting management approval.";
    if (current.state === "authorized") return "Operations handover is complete.";
    if (!current.invoice_locked) return "Lock the invoice before handing over to Operations.";
    if (Number(current.invoice_total || 0) <= 0) return "A positive locked invoice is required before handover.";
    const missingCount = current.missing_address_query_part_ids?.length || 0;
    if (missingCount) return `Select and save a delivery address for ${missingCount} order ${missingCount === 1 ? "line" : "lines"}.`;
    if (!current.lines?.length) return "No active quoted order lines are available for handover.";
    if (current.state === "rejected") return current.handover?.approval_decision_reason || "The previous handover was rejected; correct it and submit again.";
    return current.eligible ? "Ready for Operations handover." : "The order does not yet meet the server's handover requirements.";
  };

  useEffect(() => {
    onStatusChange?.({
      loading,
      eligible: !!projection?.eligible,
      state: projection?.state || null,
      reason: error && !projection ? "Handover readiness could not be loaded." : getBlockedReason(projection),
    });
  }, [projection, loading, error, onStatusChange]);

  if (loading && !projection) return <div style={{ display: "flex", justifyContent: "center", padding: 48 }}><Spin /></div>;
  if (error && !projection) return <Alert type="error" showIcon message="Operations handover could not be loaded" description={error.message} action={<Button onClick={() => reload()}>Retry</Button>} />;
  if (!projection) return <Empty description="No handover information" />;

  const state = projection.state;
  const allAddressesSelected = projection.lines.length > 0 && projection.lines.every(line => addressValues[line.query_part_id]);
  const addressesDirty = projection.lines.some(line => Number(addressValues[line.query_part_id] || 0) !== Number(line.address_id || 0));
  const approvalApproved = projection.handover?.approval_status === "approved";

  const persistAddresses = async () => {
    const selections = projection.lines.map(line => ({ query_part_id: line.query_part_id, customer_address_id: addressValues[line.query_part_id] }));
    try { await saveAddresses(selections); message.success("Delivery addresses saved."); }
    catch (err) { message.error(err.response?.data?.message || err.message); }
  };

  const submit = async values => {
    try {
      if (!idempotencyKeyRef.current) idempotencyKeyRef.current = makeKey(queryPk);
      const result = await handover({
        authorization_basis: values.authorization_basis,
        authorization_terms: values.terms ? { note: values.terms } : null,
        priority: values.priority,
        requested_owner_id: null,
        idempotency_key: idempotencyKeyRef.current,
      });
      idempotencyKeyRef.current = null;
      message.success(result.message);
    } catch (err) { message.error(err.response?.data?.message || err.message); }
  };

  return (
    <div style={{ paddingTop: embedded ? 0 : 12, display: "flex", flexDirection: "column", gap: 14 }}>
      <Alert
        type={state === "authorized" ? "success" : state === "awaiting_approval" ? "warning" : state === "rejected" ? "error" : "info"}
        showIcon
        message={state === "authorized" ? "Handed over to Operations" : state === "awaiting_approval" ? "Awaiting management approval" : state === "rejected" ? "Handover rejected" : "Prepare Operations handover"}
        description={state === "rejected" ? (projection.handover?.approval_decision_reason || "Correct the order and submit a new handover.") : state === "authorized" ? "Commercial responsibility has transferred to Operations." : state === "awaiting_approval" ? "Commercial changes are locked while the approval is pending." : projection.eligible ? "All requirements are complete. Review the authorization and hand over to Operations." : getBlockedReason(projection)}
      />

      <Descriptions data-handover-tour="financial-summary" size="small" bordered column={3}>
        <Descriptions.Item label="Invoice">{money(projection.invoice_total)}</Descriptions.Item>
        <Descriptions.Item label="Received">{money(projection.received_amount)}</Descriptions.Item>
        <Descriptions.Item label="Due">{money(projection.due_amount)}</Descriptions.Item>
      </Descriptions>

      <Card data-handover-tour="addresses" size="small" title={`Delivery instructions (${projection.lines.length})`} extra={canSubmit && !projection.sales_locked && <Button type="primary" size="small" loading={saving} disabled={!allAddressesSelected} onClick={persistAddresses}>Save addresses</Button>}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {projection.lines.map(line => (
            <div key={line.query_part_id} style={{ display: "grid", gridTemplateColumns: "minmax(160px, 1fr) minmax(260px, 1.5fr)", gap: 12, alignItems: "center" }}>
              <div>
                <Typography.Text strong>{line.part_name || "Part"}</Typography.Text>
                <div><Typography.Text type="secondary" style={{ fontSize: 12 }}>{line.part_number || "No part number"} · Qty {line.quantity}</Typography.Text></div>
              </div>
              <Select
                value={addressValues[line.query_part_id]}
                disabled={projection.sales_locked || !canSubmit}
                placeholder="Select delivery address"
                options={addresses.map(address => ({ value: address.id ?? address.address_id, label: addressLabel(address) }))}
                onChange={value => setAddressValues(current => ({ ...current, [line.query_part_id]: value }))}
                showSearch
                optionFilterProp="label"
              />
            </div>
          ))}
          {!addresses.length && <Alert type="warning" showIcon message="This customer has no saved addresses." />}
        </div>
      </Card>

      {state === "authorized" ? (
        <Card data-handover-tour="authorized" size="small"><Space><CheckCircleOutlined style={{ color: "#52c41a" }} /><Typography.Text strong>Operations order #{projection.operations_order_id}</Typography.Text>{projection.assigned_operations_user_id && <Tag color="blue">Owner assigned</Tag>}</Space></Card>
      ) : state === "awaiting_approval" ? (
        <Card data-handover-tour="approval" size="small">
          <Space direction="vertical">
            <Typography.Text>Approval request #{projection.handover?.approval_request_id || "—"}</Typography.Text>
            {approvalApproved && can("approvals.apply_approved_operation") && <Button type="primary" loading={saving} onClick={() => applyApproved(projection.handover.id).then(r => message.success(r.message)).catch(err => message.error(err.response?.data?.message || err.message))}>Apply approved handover</Button>}
          </Space>
        </Card>
      ) : (
        <Card data-handover-tour="authorization" size="small" title="Authorization" style={!projection.eligible ? { opacity: .68, filter: "saturate(.5)" } : undefined}>
          <Form form={form} layout="vertical" onFinish={submit}>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 1fr) 120px", gap: 12 }}>
              <Form.Item name="authorization_basis" label="Authorization basis" rules={[{ required: true }]}>
                <Select disabled={!projection.eligible} options={(projection.allowed_bases || []).map(value => ({ value, label: AUTHORIZATION_LABELS[value] || value }))} />
              </Form.Item>
              <Form.Item name="priority" label="Priority" rules={[{ required: true }]}><InputNumber disabled={!projection.eligible} min={1} max={255} style={{ width: "100%" }} /></Form.Item>
            </div>
            <Form.Item name="terms" label="Authorization terms / note"><Input.TextArea disabled={!projection.eligible} rows={3} placeholder="Record COD, pay-later, or exceptional management terms" /></Form.Item>
            {canSubmit && <span data-handover-tour="submit"><Button type="primary" htmlType="submit" icon={<SendOutlined />} loading={saving} disabled={!projection.eligible || addressesDirty}>Hand over to Operations</Button></span>}
            {!canSubmit && <Alert type="info" showIcon message="Your access preset does not allow Operations handover." />}
            {addressesDirty && <Typography.Text type="warning" style={{ marginLeft: 10 }}>Save the changed addresses first.</Typography.Text>}
            {!projection.invoice_locked && <Typography.Text type="danger" style={{ marginLeft: 10 }}>Lock the invoice before handover.</Typography.Text>}
            {!projection.eligible && <div style={{ marginTop: 8 }}><Typography.Text type="secondary">{getBlockedReason(projection)}</Typography.Text></div>}
          </Form>
        </Card>
      )}
    </div>
  );
}
